import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { CalendarCell } from "./CalendarCell";
import styles from "./DecadeView.module.less";
import {
	yearsInADecade,
	isBetween
} from "client/DateAndTime/dateUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { KeyEventArguments } from "../TextInput/TextInput";
import { Key } from "client/Misc/commonTypes";
import { CalendarNavigationKey, isCalendarNavigationKey } from "./Calendar";

export type DecadeViewHandle = {
	focusDate(date: DateOnly): void;
};

export type DecadeViewProps = {
	decade: number;
	today: DateOnly;
	highlightedDate: DateOnly | null;
	onSelect(year: number): void;
	onViewRangeChange: (modifier: 1 | -1) => void;
	minYear?: number;
	maxYear?: number;
	ref: Ref<DecadeViewHandle>;
};

export const DecadeView = (props: DecadeViewProps): ReactElement => {
	const {
		decade,
		today,
		highlightedDate,
		onSelect,
		onViewRangeChange,
		minYear,
		maxYear,
		ref
	} = props;

	const [hoverIndex, setHoverIndex] = useState<number | null>();
	const [focusableYearName, setFocusableYearName] = useState<number | undefined>(undefined);

	const years: readonly Year[] = useMemo(
		(): readonly Year[] => {
			const startYear: number = decade * yearsInADecade;

			return range(-1, yearsInADecade + 1)
				.map((x): Year => {
					const yearNumber: number = startYear + x;
					const isAfterMin: boolean = isNil(minYear) || yearNumber >= minYear;
					const isBeforeMax: boolean = isNil(maxYear) || yearNumber <= maxYear;

					return {
						name: yearNumber,
						start: new DateOnly(yearNumber, 0, 1),
						end: new DateOnly(yearNumber + 1, 0, 1),
						isSelectable: isAfterMin && isBeforeMax
					};
				});
		},
		[
			decade,
			minYear,
			maxYear
		]
	);

	const computedFocusableYearName = useMemo(
		(): number => {
			if (isNil(focusableYearName) || !isInRange(focusableYearName, decade)) {
				return years[0].name;
			}

			return focusableYearName;
		},
		[
			decade,
			focusableYearName,
			years
		]
	);

	const itemRefs: ReadonlyRefObject<Map<number, HTMLElement | null>> =
		useRef<Map<number, HTMLElement | null>>(new Map());

	const setRowRef = useCallback(
		(item: HTMLElement | null, yearName: number): void => {
			if (isNil(item)) {
				return;
			}

			itemRefs.current
				.set(yearName, item);
		},
		[]
	);

	const focusItemHandler = useCallback(
		(targetYear: number): void => {
			setFocusableYearName(targetYear);

			// The setTimeout is used to delay focusing the target month until after the view range has changed
			setTimeout(
				(): void => {
					itemRefs.current
						?.get(targetYear)
						?.focus();
				},
				1
			);
		},
		[]
	);

	const movementHandler = (args: KeyEventArguments, selectedYear: number): boolean => {
		if (isNil(args.key) || !isCalendarNavigationKey(args.key)) {
			return false;
		}

		const keyToOffset: Record<CalendarNavigationKey, number> = {
			[Key.ArrowDown]: DECADE_VIEW_COLUMN_AMOUNT,
			[Key.ArrowUp]: -DECADE_VIEW_COLUMN_AMOUNT,
			[Key.ArrowLeft]: -1,
			[Key.ArrowRight]: 1
		};

		const offset: number = keyToOffset[args.key];
		const targetYear: number = selectedYear + offset;

		if (!isInRange(targetYear, decade)) {
			onViewRangeChange(offset > 0 ? 1 : -1);
		}

		focusItemHandler(targetYear);

		return true;
	};

	useImperativeHandle(
		ref,
		(): DecadeViewHandle => ({
			focusDate: (date: DateOnly): void => {
				focusItemHandler(date.year);
			}
		}),
		[focusItemHandler]
	);

	return (
		<div
			className={styles.root}
			style={{
				"--column-amount": DECADE_VIEW_COLUMN_AMOUNT
			}}
			role="grid"
		>
			{Array.from({ length: DECADE_VIEW_ROW_AMOUNT }).map((_, rowIndex) => {
				const startIndex = rowIndex * DECADE_VIEW_COLUMN_AMOUNT;
				const cells = years.slice(startIndex, startIndex + DECADE_VIEW_COLUMN_AMOUNT);

				return (
					<div
						key={rowIndex}
						className={styles.row}
						role="row"
					>
						{cells.map((x, i) => {
							const itemIndex = startIndex + i;

							return (
								<CalendarCell
									trackingId={generateTrackingId("year", `|${x.start.year}`)}
									kind="year"
									key={i}
									isOutOfRange={isOtherDecade(x.start, decade)}
									isActive={isNotNil(highlightedDate) && isBetween(
										highlightedDate,
										x.start,
										x.end
									)}
									isToday={isBetween(
										today,
										x.start,
										x.end
									)}
									isDisabled={!x.isSelectable}
									onClick={() => onSelect(x.start.year)}
									onMouseEnter={() => setHoverIndex(itemIndex)}
									onMouseLeave={() => setHoverIndex(null)}
									isHovered={hoverIndex === itemIndex}
									tabIndex={x.name === computedFocusableYearName ? 0 : -1}
									ref={(el) => setRowRef(el, x.name)}
									onKeyDown={args => movementHandler(args, x.name)}
								>
									{ x.name }
								</CalendarCell>
							);
						})}
					</div>
				);
			})}
		</div>
	);
};
DecadeView.displayName = "DecadeView";

const DECADE_VIEW_COLUMN_AMOUNT = 4;
const DECADE_VIEW_ROW_AMOUNT: number = 12 / DECADE_VIEW_COLUMN_AMOUNT;

type Year = {
	name: number;
	start: DateOnly;
	end: DateOnly;
	isSelectable: boolean;
};

function isOtherDecade(
	value: DateOnly,
	decade: number
): boolean {
	return Math.floor(value.year / yearsInADecade) !== decade;
}

function isInRange(
	year: number,
	decade: number
): boolean {
	const decadeStartYear: number = decade * yearsInADecade - 1;
	const decadeEndYear: number = decadeStartYear + yearsInADecade + 1;

	return year >= decadeStartYear && year <= decadeEndYear;
}